import React, {useEffect, useState} from "react";
import BaseScreen from "../base/BaseScreen";
import styled from "styled-components";
import {
    inter, lemonMilk, white, primaryBlack, primaryPurple, primaryPink, secondaryRed, secondaryGreenLight, secondaryPinkLight,
    BasicGrid, Button, SmallHeaderTitle, SmallerHeaderTitle,
    Container
} from "../../Theme";
import Api from "../../util/Api";
import {useHistory} from "react-router-dom";
import {device} from "../../constants/breakpoints";
import showExecutiveFunctionProgressPopup from "../../popups/showExecutiveFunctionProgressPopup";
import {ConfigurableLoader, LoaderOverlay} from "../../components/Loader";
import rocketIcon from "../../resources/images/rocketIcon.png";
import AppStorage, {StorageKey} from "../../util/AppStorage";
import {showNativeNavigation} from "../../AppInterface";
import {determineSkill} from "../../util/Helpers";
import {useGlobalState} from "../../util/GlobalState";
import {UserType} from "../../constants/enums";
import { ProgressBarChart } from "../../components/ProgressBarChart/ProgressBarChart";
import { isInReadOnlyMode } from "../../App";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { PupilSchoolYearSelector } from "../../components/PupilSchoolYearSelector/PupilSchoolYearSelector";
import { usePageDataContext } from "../../contexts/PageDataContext";
import {getContextParams} from "../../util/ParamsHelpers";
import { HomepagePopup } from "../../components/HomepagePopup/HomepagePopup";

function PlanOfActionOverview() {
    const [periods, setPeriods] = useState([]);
    const [lessonUpUrl, setLessonUpUrl] = useState(null);
    const [requests, setRequests] = useState({
        periods: false,
        lessonUpUrl: false,
    })
    const [isLoading, setIsLoading] = useState(false)
    const history = useHistory();
    const [selectedPupil,] = useGlobalState("selectedPupil");
    const [selfKnowledgeQuestionnaireData, setSelfKnowledgeQuestionnaireData] = useState(null);
    const {selectedSchoolYear, isSchoolYearContextReady} = usePageDataContext();

    useEffect(() => {
        if (!isSchoolYearContextReady) return;

        setIsLoading(true);
        setRequests({periods: false, lessonUpUrl: false});
        showNativeNavigation();
        getPeriods();
        getLessonUpUrlFromApi();
        getSelfKnowledgeQuestionnaire();
        //eslint-disable-next-line
    }, [selectedPupil, selectedSchoolYear?.value, isSchoolYearContextReady]);

    useEffect(() => {
        if (Object.values(requests).every(r => r === true)) {
            setIsLoading(false)
        }
    }, [requests]);

    const content = (
        <PlanOfActionOverviewRoot>

            {(isLoading || !isSchoolYearContextReady) &&
                <LoaderOverlay>
                    <ConfigurableLoader delay={false}/>
                </LoaderOverlay>
            }

            <HomepagePopup />
            
            <PageHeader>
                <PageHeader.Center>
                    <SmallHeaderTitle>EF in beeld</SmallHeaderTitle>
                </PageHeader.Center>
                <PageHeader.End>
                    <PupilSchoolYearSelector />
                </PageHeader.End>
            </PageHeader>

            {periods.length !== 0 &&
                <PlanOfActionOverviewWrapper>

                    <GridWrapper>
                        <MobileProgressButton onClick={() => handleOnMobileProgressButtonClick()}>
                            <ProgressButtonText>Bekijk voortgangsoverzicht</ProgressButtonText>
                        </MobileProgressButton>

                        <BasicGrid
                            maxWidth={"100%"}
                            rowGap={"40px"}
                            columnGap={"40px"}
                            columnDivision={"1fr 1fr"}
                            laptopRowGap={"30px"}
                            laptopColumnGap={"30px"}
                            mobileRowGap={"16px"}
                            mobileColumnGap={"16px"}
                            mobileColumnDivision={"1fr 1fr"}
                        >
                            {periods.map((period, index) => {
                                return <PlanOfActionOverviewCard
                                    key={period.id}
                                    period={period}/>
                            })}
                        </BasicGrid>

                        {/* Jezelf in Beeld */}
                        {selfKnowledgeQuestionnaireData && (
                            <LinkBlock>
                                <SmallerHeaderTitle>Breng jezelf in beeld</SmallerHeaderTitle>
                                <LinkBlockDescription>De komende tijd ga je aan de slag om dé manieren te vinden waarmee jij het beste uit je schooltijd kunt halen. Door jezelf in kaart te brengen, kun je persoonlijk groeien!</LinkBlockDescription>
                                <LinkBlockButton onClick={() => navigateToLink("/ef-voortgang/jezelf-in-beeld")}>
                                    {selfKnowledgeQuestionnaireData?.completed || isInReadOnlyMode() ? "Bekijk ingevulde vragenlijst" : "Invullen"}
                                </LinkBlockButton>
                            </LinkBlock>
                        )}

                    </GridWrapper>

                    <PlanOfActionProgressCard periods={periods} popup={false} />

                </PlanOfActionOverviewWrapper>
            }
        </PlanOfActionOverviewRoot>
    );

    function PlanOfActionProgressCard(props) {

        return (
            <ProgressCard popup={props.popup}>
                <div>
                    <ProgressCardTitle>Voortgangsoverzicht</ProgressCardTitle>

                    <ProgressListLabelWrapper>
                        <ProgressListLabel>Executieve Functies</ProgressListLabel>
                        <ProgressListScoreLabelWrapper>
                            <ProgressListLabel>P1</ProgressListLabel>
                            <ProgressListLabel>P2</ProgressListLabel>
                            <ProgressListLabel>P3</ProgressListLabel>
                            <ProgressListLabel>P4</ProgressListLabel>
                        </ProgressListScoreLabelWrapper>
                    </ProgressListLabelWrapper>

                    <ProgressList>
                        {
                            (periods[0].scoresPerExecutiveFunction ?? []).map((ef, i) => {
                                return (
                                    <ProgressListItem
                                        key={ef.title}
                                        index={i + 1}
                                        title={ef.title}
                                        skillScale={{beginner: periods[0].beginner, developing: periods[0].developing, mastered: periods[0].mastered}}
                                        scores={props.periods.map((p) => p.scoresPerExecutiveFunction[i].totalScore)}
                                    />
                                )
                            })
                        }

                    </ProgressList>
                </div>
                {(periods[0]?.lessonUpEnabled && lessonUpUrl) && (
                    <PracticeButton href={lessonUpUrl} target={"_blank"} rel="noreferrer">Digitaal oefenen<RocketIcon src={rocketIcon}/></PracticeButton>
                )}
            </ProgressCard>
        );
    }

    function PlanOfActionOverviewCard(props) {
        const hasFilledInQuestionnaire = props?.period?.scoresPerExecutiveFunction?.every(item => item.totalScore !== null && item.totalScore !== undefined);

        return (
            <PlanOfActionOverviewCardRoot onClick={() => navigateToPeriod()}>
                <OverviewCardImageOuter>
                    {!isLoading ? (
                        <OverviewCardChart>
                            <ProgressBarChart
                                data={props?.period}
                                variant="preview"
                                previewEmpty={!hasFilledInQuestionnaire}
                            />
                        </OverviewCardChart>
                    ) : (
                        <OverviewCardLoading />
                    )}
                </OverviewCardImageOuter>
                <OverviewCardButton>
                    <OverViewCardButtonText>{props.period.title}</OverViewCardButtonText>
                </OverviewCardButton>
            </PlanOfActionOverviewCardRoot>
        );

        function navigateToPeriod() {
            if (props.period.scoresPerExecutiveFunction.filter(ef => ef.totalScore == null).length) {
                //If the user hasn't finished this period's questionnaire yet
                history.push("/ef-voortgang/" + props.period.id + "/vragenlijst", {period: props.period});
            } else {
                history.push("/ef-voortgang/" + props.period.id, {period: props.period})
            }
        }
    }

    function ProgressListItem({skillScale, ...props}) {
        return (
            <ProgressListItemRoot>
                <ProgressListItemTitle>{props.index}. {props.title}</ProgressListItemTitle>
                <ScoreRow>
                    {props.scores.map((totalScore, index) => {
                        const determinedSkill = isLoading ? {} : determineSkill(totalScore, skillScale);

                        return <ScoreIndicatorIcon key={index} totalScore={totalScore} determinedSkill={determinedSkill} />
                    })}
                </ScoreRow>
            </ProgressListItemRoot>
        );
    }

    return (
        <BaseScreen
            activeMenuItem={"EF in beeld"}
            content={content}
        />
    )

    function getPeriods() {
        const params = getContextParams();
        if (!params) return;

        const config = {
            params: {
                ...params,
            }
        }

        if (AppStorage.get(StorageKey.PUPIL_ID) || AppStorage.get(StorageKey.USER_TYPE) === UserType.PUPIL) {
            Api.get("/periods", (response) => {
                setPeriods(response.data);
                onFinished()
            }, null, onFinished, onFinished, config)
        }

        function onFinished() {
            setRequests(prevRequests => ({
                ...prevRequests,
                periods: true,
            }));
        }
    }

    function getSelfKnowledgeQuestionnaire() {
        const params = getContextParams();
        if (!params) return;

        const config = {
            params: {
                ...params,
            }
        }

        Api.get("/selfKnowledgeQuestionnaire", (response) => {
            if (response.data.length > 0) {
                setSelfKnowledgeQuestionnaireData(response.data[0]);
            } else {
                setSelfKnowledgeQuestionnaireData(null);
            }
        }, null, null, null, config);
    }

    function handleOnMobileProgressButtonClick() {
        const content = (<PlanOfActionProgressCard popup={true} periods={periods}/>)
        showExecutiveFunctionProgressPopup(content);
    }

    function getLessonUpUrlFromApi() {
        Api.get('configuration', (response) => {
            setLessonUpUrl(response.data[0].lessonUpUrl)
            onFinished()
        }, null, onFinished, onFinished)

        function onFinished() {
            setRequests(prevRequests => ({
                ...prevRequests,
                lessonUpUrl: true,
            }));
        }
    }

    function navigateToLink(url) {
        history.push(url);
    }

}

export function ScoreIndicatorIcon({determinedSkill, pdfExport = false, ...props}) {
    return (
        <ScoreIndicatorRoot $color={determinedSkill?.color} {...props}>
            <ScoreNumber $pdfExport={pdfExport}>
                {determinedSkill?.shortLabel}
            </ScoreNumber>
        </ScoreIndicatorRoot>
    );
}

export const PlanOfActionOverviewRoot = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
`;

export const PlanOfActionOverviewWrapper = styled(Container)`
    width: 100%;
    padding: 40px 50px 40px 72px;
    display: flex;
    flex-direction: row;
    gap: 50px;
    justify-content: space-between;
    align-items: flex-start;

    @media screen and ${device.laptopL} {
        padding: 30px 17px;
        justify-content: center;
        flex-direction: column;
        align-items: center;
    }

    @media screen and ${device.mobileL} {
        padding: 17px;
    }
`;

export const PlanOfActionOverviewCardRoot = styled.div`
    width: 100%;
    cursor: pointer;
    display: flex;
    flex-direction: column;
`;

const OverviewCardImageOuter = styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 3 / 2;
    background-color: white;

    @supports not (aspect-ratio: 3 / 2) {
        overflow: hidden;
        padding-top: 66.67%;
    }
`;

const OverviewCardChart = styled.div`
    display: block;
    width: 100%;
    height: 100%;
    pointer-events: none;
    container-type: inline-size;

    @supports not (aspect-ratio: 3 / 2) {
        position: absolute;
        inset: 0;
    }
`;

const OverviewCardLoading = styled.div`
    display: block;
    width: 100%;
    height: 100%;

    @supports not (aspect-ratio: 3 / 2) {
        position: absolute;
        inset: 0;
    }
`;

export const OverviewCardButton = styled(Button)`
    width: 100%;
    max-width: 100%;
    height: 100%;
    background: ${primaryPink};
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding: 14px;
    z-index: 1;
`;

export const ProgressCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${props => props.popup ? "20px" : "40px"};
    min-width: ${props => props.popup ? "unset" : "395px"};
    height: 100%;
    flex: 1;
    background: ${white};
    padding: ${props => props.popup ? "none" : "30px 40px 50px 40px"};

    @media screen and ${device.laptopL} {
        display: ${props => props.popup ? "flex" : "none"};
    }
`;

export const OverViewCardButtonText = styled.div`
    font-family: ${lemonMilk};
    font-weight: 400;
    font-size: 15px;
    color: ${white};
`;

export const ProgressCardTitle = styled.h2`
    font-family: ${inter};
    font-weight: 600;
    font-size: 16px;
    color: ${primaryBlack};
    margin: 0 0 35px 0;
    text-align: center;
`;

export const ProgressList = styled.div`
    display: flex;
    flex-direction: column;
`;

export const ProgressListItemRoot = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 50px;
    border-bottom: 1px solid rgba(86, 89, 146, 0.1);

    &:last-of-type {
        border-bottom: unset;
    }
`;

export const ProgressListItemTitle = styled.div`
    font-family: ${inter};
    font-weight: 400;
    font-size: 16px;
    color: ${primaryBlack};
`;

export const ScoreRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 9px;
`;

export const ScoreIndicatorRoot = styled.div`
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({$color}) => $color ? $color : secondaryGreenLight};
    border-radius: 50%;
`;

export const ScoreNumber = styled.span`
    font-family: ${inter};
    font-weight: 600;
    font-size: 16px;
    color: ${white};
    ${({$pdfExport}) => $pdfExport && "margin-bottom: 2px;"} // Add small margin to the export to center the text
`;

export const ProgressListLabelWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 5px
`;

export const ProgressListScoreLabelWrapper = styled.div`
    display: flex;
    flex-direction: row;
    gap: 9px;
`;

export const ProgressListLabel = styled.div`
    font-family: ${inter};
    font-weight: 400;
    font-size: 14px;
    color: ${primaryBlack};
    opacity: 0.7;
    min-width: 20px;
    text-align: center;
`;

export const MobileProgressButton = styled(Button)`
    background: ${secondaryRed};
    width: 100%;
    max-width: 100%;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    margin-bottom: 30px;
    cursor: pointer;
    display: none;

    @media screen and ${device.laptopL} {
        display: flex;
        margin-bottom: 16px;
    }
`;

export const ProgressButtonText = styled.div`
    font-family: ${inter};
    font-weight: 600;
    font-size: 15px;
    color: ${white};
`;

export const PracticeButton = styled.a`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: white;
    font-family: ${lemonMilk};
    font-size: 15px;
    font-weight: normal;
    color: ${primaryPurple};
    width: 100%;
    cursor: pointer;
    height: 35px;
    border: ${primaryPurple} 1px solid;
    border-radius: 40px;

    &:hover {
        cursor: pointer;
        color: ${primaryPurple};
    }
`;

export const RocketIcon = styled.img`
    width: 18px;
    height: 18px;
`
export const GridWrapper = styled.div`
    max-width: 900px;
    width: 100%;
`;

export const LinkBlock = styled.div`
    background: ${secondaryPinkLight};
    font-family: ${inter};
    padding: 24px;
    margin-top: 40px;
`

export const LinkBlockDescription = styled.p`
    color: ${primaryBlack};
    margin: 12px 0;
`

export const LinkBlockButton = styled(Button)`
    font-family: ${lemonMilk};
    font-size: 15px;
    font-weight: normal;
    color: ${white};
    padding: 14px 20px;
    background: ${secondaryRed};
    width: auto;
    margin-top: 12px;
    max-width: 100%;
`


export default PlanOfActionOverview;

